import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiLoader } from "@miplatform/components";
import { BUTTON_VARIANTS, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { LoaderType } from "@miplatform/types";
import { mdiAccountPlusOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import {
  type StagedWorkOrderAssignment,
  getExpectedDurationMinutes,
} from "../NHCPriorityScheduler/useAssignmentStaging";
import { IAssignableUser } from "../NhcCalendar.types";
import { AssignableUserCard } from "./AssignableUserCard";

export const AssignableUserContainer = ({
  assignableUsersAvailableTime,
  isLoading,
  selectedUserId,
  activeAssignableUserIds = [],
  stagedByUserId = {},
  isAutoAssignLoading = false,
  isAutoAssignActive = false,
  showAutoAssign = true,
  onUserSelect,
  onAddAssignableUser,
  onAutoAssign,
}: Readonly<AssignableUserContainerProps>) => {
  const { classes, theme } = useStyles();

  const { stagedDurationHoursByUserId, stagedCountByUserId } = useMemo(() => {
    const hours: Record<string, number> = {};
    const counts: Record<string, number> = {};
    for (const [userId, entries] of Object.entries(stagedByUserId)) {
      const sameDayEntries = entries.filter((e) => e.dayOffset === 0);
      if (sameDayEntries.length) {
        counts[userId] = sameDayEntries.length;
        const minutes = sameDayEntries.reduce(
          (sum, { attempt }) => sum + getExpectedDurationMinutes(attempt),
          0
        );
        hours[userId] = minutes / 60;
      }
    }
    return {
      stagedDurationHoursByUserId: hours,
      stagedCountByUserId: counts,
    };
  }, [stagedByUserId]);

  const selectedUserName = useMemo(
    () =>
      assignableUsersAvailableTime.find(
        (u: IAssignableUser) => u.id === selectedUserId
      )?.name,
    [assignableUsersAvailableTime, selectedUserId]
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          fontWeight={theme.fontWeight.semiBold}
          color={theme.palette.text.primary}
          noWrap
        >
          Assignable Users:
          {selectedUserName && (
            <Typography
              component="span"
              variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
              fontWeight={theme.fontWeight.semiBold}
              color={theme.palette.primary.main}
            >
              {` Assigning to ${selectedUserName}`}
            </Typography>
          )}
        </Typography>

        {showAutoAssign && (
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            title="Auto Assign"
            startIcon={{
              path: mdiAccountPlusOutline,
            }}
            disabled={isAutoAssignLoading || isAutoAssignActive}
            onClick={() => onAutoAssign?.()}
            className={classes.autoAssignButton}
          />
        )}
      </Box>
      {isLoading ? (
        <MiLoader isLoading={isLoading} type={LoaderType.Circular} />
      ) : (
        <Box className={classes.list}>
          {assignableUsersAvailableTime.map((user) => (
            <AssignableUserCard
              key={user.id}
              name={user.name ?? ""}
              availableTime={Math.max(
                0,
                user.availableTime - (stagedDurationHoursByUserId[user.id] ?? 0)
              )}
              assignedWorkOrderCount={
                user.assignedWorkOrdersCount +
                (stagedCountByUserId[user.id] ?? 0)
              }
              stagedWorkOrderCount={stagedByUserId[user.id]?.length ?? 0}
              selected={user.id === selectedUserId}
              showAddButton={!activeAssignableUserIds.includes(user.id)}
              onClick={() => onUserSelect(user.id)}
              onAdd={() => onAddAssignableUser?.(user.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

type AssignableUserContainerProps = {
  assignableUsersAvailableTime: IAssignableUser[];
  isLoading: boolean;
  selectedUserId?: string;
  activeAssignableUserIds?: string[];
  stagedByUserId?: Record<string, StagedWorkOrderAssignment[]>;
  isAutoAssignLoading?: boolean;
  isAutoAssignActive?: boolean;
  showAutoAssign?: boolean;
  onUserSelect: (userId: string) => void;
  onAddAssignableUser?: (userId: string) => void;
  onAutoAssign?: () => void | Promise<void>;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflow: "auto",
  },
  list: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(2),
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 30,
  },
  autoAssignButton: {
    position: "absolute",
    right: theme.spacing(2),
    height: 30,
    padding: theme.spacing(2, 2),
    borderWidth: 1.5,
  },
}));
